import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { StickyPositionDirective } from './sticky-position.directive';

@Directive({
  selector: '[resizeColumn]',
  standalone: true,
})
export class ResizeColumnDirective {
  mouseMoveListener: () => void;
  mouseUpListener: () => void;

  startX: number = 0;
  startWidth: number = 0;
  tdList: any[] = [];
  maxOneRowElement: HTMLElement;
  hasStickyColumn: boolean;
  innerWidth: number;

  @Input() stickyPositionDirective: StickyPositionDirective;
  @Input() pageName: string;
  @Input() size: number;
  @Input() loading: boolean;
  @Input() applyResizeColumn: boolean;

  @Output() columnSize = new EventEmitter<number>();

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngAfterContentInit(): void {
    let timeOut = setTimeout(() => {
      this.updateTableColumns();
      clearTimeout(timeOut);
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading) {
      let timeOut = setTimeout(() => {
        this.updateTableColumns();
        clearTimeout(timeOut);
      }, 100);
    }
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    this.innerWidth = window.innerWidth;

    if (this.innerWidth < 1024 || !this.applyResizeColumn) return;

    const target = event.target as HTMLElement;
    if (
      !target.classList.contains('resize-column-btn') &&
      !target.classList.contains('mdi-equal')
    )
      return;

    event.preventDefault();

    this.startX = event.clientX;
    this.startWidth = this.el.nativeElement.offsetWidth;

    this.mouseMoveListener = this.renderer.listen(
      'document',
      'mousemove',
      (event) => this.onMouseMove(event)
    );
    this.mouseUpListener = this.renderer.listen('document', 'mouseup', () =>
      this.onMouseUp()
    );
  }

  // Updates element width and button classes on mouse move.
  onMouseMove = (event: MouseEvent): void => {
    const resizeColumnBtns = document.querySelectorAll('.resize-column-btn');
    const currentResizeColumnBtns =
      this.el.nativeElement.querySelector('.resize-column-btn');
    const resizeColumnElement =
      this.el.nativeElement.querySelector('.resize-column');
    const dx = this.startX - event.clientX;
    let newWidth = this.startWidth + dx;

    this.getTdListByColumnName();

    if (!resizeColumnElement) return;

    const maxWidth = this.calculateMaxWidthForElement();
    newWidth = Math.min(newWidth, maxWidth);

    this.updateElementWidths(resizeColumnElement, newWidth, maxWidth);

    this.clearPopovers();
    this.applyElementStyles(newWidth, maxWidth);

    this.showResizeButtons(currentResizeColumnBtns, resizeColumnBtns);
  };

  onMouseUp = (): void => {
    const resizeColumnBtns = document.querySelectorAll('.resize-column-btn');
    const currentResizeColumnBtn =
      this.el.nativeElement.querySelector('.resize-column-btn');

    if (this.tdList?.length) {
      this.resetTableCellStyles();
    }

    this.toggleResizeColumnBtnVisibility(currentResizeColumnBtn);
    this.resetElementStyles();
    this.resetButtonStyles(resizeColumnBtns);

    this.removeEventListeners();
  };

  calculateMaxWidthForElement(): number {
    if (this.el.nativeElement.classList.contains('sticky-item')) {
      return this.calculateMaxWidth(this.innerWidth);
    }
    return 600;
  }

  updateElementWidths(
    resizeColumnElement: HTMLElement,
    newWidth: number,
    maxWidth: number
  ): void {
    this.renderer.setStyle(this.el.nativeElement, 'width', `${newWidth}px`);
    this.renderer.setStyle(this.el.nativeElement, 'max-width', `${maxWidth}px`);
    this.renderer.setStyle(resizeColumnElement, 'width', `${newWidth}px`);
    this.renderer.setStyle(resizeColumnElement, 'max-width', `${maxWidth}px`);
    this.columnSize.emit(newWidth);
  }

  clearPopovers(): void {
    document
      .querySelectorAll('message-popover')
      .forEach((popover) => popover.remove());
  }

  applyElementStyles(newWidth: number, maxWidth: number): void {
    this.renderer.addClass(this.el.nativeElement, 'lg:!tw-bg-blue-50');
    this.renderer.addClass(this.el.nativeElement, 'lg:dark:!tw-bg-primary-950');
    this.renderer.addClass(this.el.nativeElement, '!tw-text-primary-600');

    this.tdList?.forEach((td) => {
      this.handleTdContent(td, newWidth, maxWidth);

      this.renderer.setStyle(td, 'width', `${newWidth}px`);
      this.renderer.addClass(td, 'lg:!tw-bg-[#f8fbff]');
      this.renderer.addClass(td, 'lg:dark:!tw-bg-[#272c4b]');
      this.renderer.addClass(td, 'resize-border');

      if (this.el.nativeElement.classList.contains('sticky-item')) {
        this.updateStickyItemStyles(td, maxWidth);
      }

      if (this.hasStickyColumn) {
        this.stickyPositionDirective.calculateAndSetStickyPosition();
      }
    });
  }

  updateStickyItemStyles(td: HTMLElement, maxWidth: number): void {
    if (this.maxOneRowElement) {
      this.renderer.setStyle(
        this.maxOneRowElement,
        'max-width',
        `${maxWidth - 40}px`
      );
    }

    this.renderer.addClass(this.el.nativeElement, 'tw-z-[2]');
    this.renderer.addClass(td, 'tw-z-[2]');

    this.renderer.removeClass(td, 'lg:dark:!tw-bg-dark-300');
    this.renderer.removeClass(td, 'lg:!tw-bg-[#f6f7f8]');
    this.renderer.removeClass(td, 'solid-border-before');
  }

  showResizeButtons(
    currentResizeColumnBtns: HTMLElement,
    resizeColumnBtns: NodeListOf<Element>
  ): void {
    this.renderer.removeClass(currentResizeColumnBtns, 'tw-hidden');
    this.renderer.removeClass(currentResizeColumnBtns, 'group-hover:tw-block');

    resizeColumnBtns.forEach((btn) =>
      btn.classList.remove('group-hover:tw-block')
    );
  }

  resetTableCellStyles(): void {
    this.tdList?.forEach((td) => {
      if (td) {
        this.renderer.removeClass(td, 'lg:!tw-bg-[#f8fbff]');
        this.renderer.removeClass(td, 'lg:dark:!tw-bg-[#272c4b]');
        this.renderer.removeClass(td, 'resize-border');

        if (
          this.el.nativeElement?.classList.contains('sticky-item') &&
          !td?.classList.contains('empty-td')
        ) {
          this.applyStickyItemStyles(td);
        }
      }
    });
  }

  applyStickyItemStyles(td: HTMLElement): void {
    // add class
    this.renderer.addClass(td, 'lg:dark:!tw-bg-dark-300');
    this.renderer.addClass(td, 'lg:!tw-bg-[#f6f7f8]');
    this.renderer.addClass(td, 'solid-border-before');

    // remove class
    this.renderer.removeClass(td, 'tw-z-[2]');
    this.renderer.removeClass(this.el.nativeElement, 'tw-z-[2]');
  }

  toggleResizeColumnBtnVisibility(btn: HTMLElement): void {
    if (btn) {
      this.renderer.addClass(btn, 'tw-hidden');
      this.renderer.addClass(btn, 'group-hover:tw-block');
    }
  }

  resetElementStyles(): void {
    this.renderer.removeClass(this.el.nativeElement, 'lg:!tw-bg-blue-50');
    this.renderer.removeClass(
      this.el.nativeElement,
      'lg:dark:!tw-bg-primary-950'
    );
    this.renderer.removeClass(this.el.nativeElement, '!tw-text-primary-600');
  }

  resetButtonStyles(buttons: NodeListOf<Element>): void {
    buttons.forEach((btn) => {
      this.renderer.addClass(btn, 'group-hover:tw-block');
    });
  }

  removeEventListeners(): void {
    if (this.mouseMoveListener) this.mouseMoveListener();
    if (this.mouseUpListener) this.mouseUpListener();
  }

  getTdListByColumnName() {
    const element = document.querySelector(`${this.pageName} .dynamic-table`);
    if (!element) return;
    const header = element.querySelectorAll('thead th');
    const rows = element.querySelectorAll('tbody tr');

    // Get the column name from the current element's 'name' attribute
    const columnName = this.el.nativeElement.getAttribute('name');
    if (!columnName) return;

    // Clear the list of 'td' elements
    this.tdList = [];

    // Find 'td' elements matching the column name
    rows.forEach((tr) => {
      if (tr.classList.contains('total-sum')) return;
      const tds = Array.from(tr.querySelectorAll('td'));
      const matchingTd = tds.find(
        (td) => td.getAttribute('name') === columnName
      );
      if (matchingTd) this.tdList.push(matchingTd);
    });

    // Count the number of sticky header columns
    const stickyHeadersCount = Array.from(header).filter((th) =>
      th.classList.contains('sticky-item')
    ).length;

    this.hasStickyColumn = stickyHeadersCount > 1;
  }

  handleTdContent(td: HTMLElement, newWidth: number, maxWidth: number): void {
    this.maxOneRowElement = td?.querySelector('.max-one-row');
    let newDiv: HTMLElement = td?.querySelector('.max-td-value');
    this.setStyle(newDiv, newWidth, maxWidth);
    if (this.maxOneRowElement)
      this.setStyle(this.maxOneRowElement, newWidth - 40, 560);
  }

  setStyle(element: HTMLElement, width: number, maxWidth: number): void {
    this.renderer.setStyle(element, 'width', `${width}px`);
    this.renderer.setStyle(element, 'max-width', `${maxWidth}px`);
  }

  updateTableColumns() {
    this.innerWidth = window.innerWidth;

    if (this.innerWidth < 1024 || !this.applyResizeColumn) return;

    this.getTdListByColumnName();
  }

  calculateMaxWidth(innerWidth: number): number {
    if (innerWidth >= 1536) return 340;
    else if (innerWidth >= 1280) return 300;
    else if (innerWidth >= 1024) return 140;

    return 600;
  }
}
